import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getDevice, getDeviceState, sendCommand, updateDevice, getDeviceEvents, getDeviceQueue, cancelDeviceCommand, getDeviceTelemetry, signalQuality } from '../services/devices';
import type { TelemetryWindow, UpdateDeviceData } from '../services/devices';
import { getDeviceBoards, boardLabel } from '../services/deviceBoards';
import { getCompanies } from '../services/companies';
import TelemetryChart from '../components/TelemetryChart';
import type { QueuedCommand } from '../services/devices';
import { getGatewaysByCompany } from '../services/gateways';
import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin, isCompanyAdmin } from '../services/users';
import styled from '@emotion/styled';
import {
  PageContainer,
  PageTitle,
  DetailCard,
  Button,
  IconButton,
  Input,
  Select,
  StatusChip,
  DeployedBadge,
  LoadingText,
  EmptyState,
  CodeBlock,
  InlineCode,
  AlertBanner,
  Row,
  Stack,
  SpaceBetween,
  MutedText,
  Caption,
  TableContainer,
  StyledTable,
  Th,
  Td,
  TableRow,
  Pagination,
  CollapsibleHeader,
  Chevron,
  CardBody,
  Subsection,
  SubsectionTitle,
  SubsectionRow,
  FormGroup,
  Label,
} from '../components/styled';
import {
  IconCircleFilled,
  IconRefresh,
  IconAlertTriangle,
  IconX,
  IconCheck,
} from '@tabler/icons-react';
import { SegmentedTabs } from '../components/SegmentedTabs';

// ─── Styled Components ───────────────────────────────────────────────────────

const DeviceHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(6)};

  p {
    margin: ${({ theme }) => theme.spacing(1)} 0 0;
  }
`;

// Top-of-page action cluster: deployment chip + Edit / Reboot / Factory Reset.
// Wraps so the buttons drop below the title on narrow viewports.
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Section = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};

  h2 {
    font-size: ${({ theme }) => theme.font.size.md};
    font-weight: ${({ theme }) => theme.font.weight.semiBold};
    color: ${({ theme }) => theme.font.color.primary};
    margin: 0;
  }
`;

const SuccessText = styled.p`
  color: ${({ theme }) => theme.color.green};
  font-size: ${({ theme }) => theme.font.size.sm};
  margin: ${({ theme }) => theme.spacing(2)} 0 0;
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.color.red};
  font-size: ${({ theme }) => theme.font.size.sm};
  margin: ${({ theme }) => theme.spacing(2)} 0 0;
`;

const TruncatedTd = styled(Td)`
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EventDataRow = styled.tr`
  background: ${({ theme }) => theme.background.secondary};
  td {
    padding: ${({ theme }) => theme.spacing(0, 3, 3)};
    border-bottom: 1px solid ${({ theme }) => theme.border.color.medium};
  }
`;


// Pulsing live indicator wrapping the Tabler IconCircleFilled. Marks the
// Live Events card header.
const LiveDot = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.color.green};
  margin-right: ${({ theme }) => theme.spacing(1)};
  animation: pulse 2s infinite;
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.3; }
  }
`;


// Health panel grid
const HealthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: ${({ theme }) => theme.spacing(4)};
`;

const HealthMetric = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const HealthLabel = styled.span`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.font.color.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const HealthValue = styled.span`
  font-size: ${({ theme }) => theme.font.size.lg};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.primary};
`;

// Inline JSON dump inside an expanded table row — strips the default CodeBlock
// margin so it slots flush into the row.
const ExpandedCodeBlock = styled.pre`
  margin: 0;
  padding: ${({ theme }) => theme.spacing(2, 3)};
  background: ${({ theme }) => theme.background.tertiary};
  color: ${({ theme }) => theme.font.color.primary};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
  font-size: ${({ theme }) => theme.font.size.xs};
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

// Small footnote text for "and N more…" overflow markers.
const FootnoteText = styled.p`
  margin: ${({ theme }) => theme.spacing(3, 0, 0)};
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.font.color.tertiary};
`;

// Search input for in-card filtering (Sent commands, etc.).
const InCardSearch = styled(Input)`
  width: 320px;
  max-width: 100%;
`;

const InCardSearchWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing(3, 4, 0)};
`;

// Caption-style label, smaller than Caption, used inline next to other labels.
const TinyMutedText = styled.span`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.font.color.tertiary};
  font-style: italic;
`;

// Small variant of HealthValue for cells that need to fit a longer label
// (Card Type, Last Wake) without wrapping. Same color, smaller text.
const SmallHealthValue = styled.span`
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.primary};
`;

// Compact action column header in queue tables. Width is bounded so the
// Cancel button stays right-aligned without stretching the column.
const ActionTh = styled(Th)`
  width: 90px;
`;

// Live Events / Sent toolbar — search + filter + dropdown in one row aligned
// to the right of the card. Replaces several inline padding/width overrides.
const InCardToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(3, 4, 0)};
  flex-wrap: wrap;
`;

const ToolbarSearch = styled(Input)`
  width: 220px;
  max-width: 100%;
`;

const ToolbarSelect = styled(Select)`
  width: auto;
  min-width: 150px;
`;

// Italicized helper note placed below a toolbar (e.g. timezone explainer
// on Live Events).
const ToolbarNote = styled.div`
  padding: ${({ theme }) => theme.spacing(1, 4, 0)};
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.font.color.tertiary};
  font-style: italic;
`;

// Truncated table cell that turns red when the row carries an error message.
const ErrorTd = styled(Td)<{ $hasError: boolean }>`
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme, $hasError }) => $hasError ? theme.color.red : 'inherit'};
`;

// Error text shown above a payload dump in an expanded queue row.
const ExpandedErrorText = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => theme.color.red};
  font-size: ${({ theme }) => theme.font.size.sm};
`;

// Compact inline rename row — a single input flanked by small save / cancel
// icon buttons. Replaces the old label + full-width primary "Save Changes"
// button so editing the name reads as a lightweight inline tweak.
const EditNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const EditNameInput = styled(Input)`
  width: 280px;
  max-width: 100%;
`;

const QueueGroup = styled.div`
  background: ${({ theme }) => theme.background.primary};
  border: 1px solid ${({ theme }) => theme.border.color.medium};
  border-radius: ${({ theme }) => theme.border.radius.md};
  overflow: hidden;
  & + & {
    margin-top: ${({ theme }) => theme.spacing(3)};
  }
`;

const QueueGroupHeader = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  width: 100%;
  padding: ${({ theme }) => theme.spacing(3, 4)};
  /* Transparent so it shows the white QueueGroup card behind it; a subtle
     tint appears only on hover. */
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.primary};
  text-align: left;
  &:hover {
    background: ${({ theme }) => theme.background.tertiary};
  }
`;

// QueueGroupChevron removed — pages use the shared <Chevron open={...} />.

const PendingBadge = styled.span<{ count: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 ${({ theme }) => theme.spacing(1)};
  border-radius: 11px;
  font-size: ${({ theme }) => theme.font.size.xs};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  background: ${({ theme, count }) => count > 0 ? theme.color.yellow : theme.background.tertiary};
  color: ${({ theme, count }) => count > 0 ? theme.font.color.primary : theme.font.color.secondary};
`;

// ─── Hardware subsection ───────────────────────────────────────────────────

const HardwareFormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0 ${({ theme }) => theme.spacing(3)};
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

// Form state is all strings so empty selects round-trip cleanly; '' becomes
// null on save so an unset battery/reader type never overwrites with ''.
interface HardwareForm {
  board_id: string;
  hardware_version: string;
  num_doors: string;
  power_type: 'battery' | 'plugin';
  battery_chemistry: string;
  reader_protocol: string;
  reader_technology: string;
  // '' = unassigned pool. Only rendered (and only sent) for super admins.
  company_id: string;
}

function toHardwareUpdate(f: HardwareForm, { includeAssignment = false } = {}): UpdateDeviceData {
  return {
    board_id: Number(f.board_id),
    hardware_version: f.hardware_version.trim() || null,
    num_doors: f.num_doors === '' ? 1 : Number(f.num_doors),
    power_type: f.power_type,
    battery_chemistry: (f.battery_chemistry || null) as UpdateDeviceData['battery_chemistry'],
    reader_protocol: (f.reader_protocol || null) as UpdateDeviceData['reader_protocol'],
    reader_technology: (f.reader_technology || null) as UpdateDeviceData['reader_technology'],
    ...(includeAssignment ? { company_id: f.company_id || null } : {}),
  };
}

// ─── Collapsible subsection ────────────────────────────────────────────────
// One pattern for every block in the unified Device card (Details /
// Connectivity / Health / Telemetry): a clickable title row with a chevron
// that toggles the body. Header accessories (a "Refreshing…" caption, the
// telemetry window pills) sit left of the chevron and don't trigger the toggle.

const SubsectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;

  /* The title doubles as the header line, so drop its default bottom margin. */
  h3 {
    margin-bottom: 0;
  }

  &:hover h3 {
    color: ${({ theme }) => theme.accent.primary};
  }
`;

const SubsectionBody = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

interface CollapsibleSubsectionProps {
  title: string;
  defaultOpen?: boolean;
  accessory?: React.ReactNode;
  children: React.ReactNode;
}

function CollapsibleSubsection({ title, defaultOpen = false, accessory, children }: CollapsibleSubsectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Subsection>
      <SubsectionHeader onClick={() => setOpen(o => !o)}>
        <SubsectionTitle>{title}</SubsectionTitle>
        <Row gap={2} align="center">
          {accessory != null && (
            <span onClick={e => e.stopPropagation()}>{accessory}</span>
          )}
          <Chevron open={open} />
        </Row>
      </SubsectionHeader>
      {open && <SubsectionBody>{children}</SubsectionBody>}
    </Subsection>
  );
}

// ─── Constants ───────────────────────────────────────────────────────────────

const EVENTS_PER_PAGE = 20;

// Direction lookup — derived from event_type, no DB column needed
// outbound = commands going TO the device (from API or gateway)
// inbound  = messages coming FROM the device (MQTT uplinks)
const OUTBOUND_TYPES = new Set(['command_sent', 'sleep_command_sent', 'command_received']);
const INBOUND_TYPES = new Set([
  'wake_up', 'sleep_confirmed', 'lock_state_changed',
  'unlock_confirmed', 'access_granted', 'access_denied',
  'device_restart', 'device_reboot', 'factory_reset', 'provision_confirmed',
  'auto_reconnect', 'auto_recovery',
  // Device-reported faults/telemetry — previously fell through to "System".
  'battery_swap', 'reader_wedged', 'cell_diagnostics',
  'recovery_boot',
  // battery_low/dead/recovered and device_offline/online are platform-derived
  // (threshold crossings / missed cadence) — they stay "System" on purpose.
]);

function getDirection(eventType: string): 'inbound' | 'outbound' | 'internal' {
  if (OUTBOUND_TYPES.has(eventType)) return 'outbound';
  if (INBOUND_TYPES.has(eventType)) return 'inbound';
  return 'internal';
}

const DIR_LABELS = {
  inbound:  'From Device',
  outbound: 'To Device',
  internal: 'System',
} as const;

const DIR_VARIANTS: Record<'inbound' | 'outbound' | 'internal', Variant> = {
  inbound:  'positive',
  outbound: 'info',
  internal: 'neutral',
};

// ─── Helper Functions ────────────────────────────────────────────────────────

function formatEventType(type: string): string {
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

type Variant = 'positive' | 'warning' | 'critical' | 'info' | 'neutral';

function severityVariant(severity: string): Variant {
  switch (severity) {
    case 'info':  return 'info';
    case 'warn':  return 'warning';
    case 'error': return 'critical';
    case 'debug':
    default:      return 'neutral';
  }
}

// ── Criticality (Live Events) ────────────────────────────────────────────────
// The Criticality column is derived from the stored severity, with three fixes
// for troubleshooting:
//   • severity is spelled two ways at the source (gateway 'warn', some API paths
//     'warning') — both are Warning, so neither renders gray by accident.
//   • firmware 'debug' traces (orchestration internals) become a distinct "Trace"
//     tier the feed hides by default — visible only when explicitly filtered.
//   • any device_restart is treated as Warning: a restart is worth a look even
//     though it's stored as info. Presentation only — the stored severity, and
//     therefore webhooks/alerts, are untouched.
type CritTier = 'critical' | 'warning' | 'info' | 'trace';

const CRIT_META: Record<CritTier, { label: string; variant: Variant }> = {
  critical: { label: 'Critical', variant: 'critical' },
  warning:  { label: 'Warning',  variant: 'warning' },
  info:     { label: 'Info',     variant: 'info' },
  trace:    { label: 'Trace',    variant: 'neutral' },
};

function criticalityTier(eventType: string | undefined, severity: string | undefined): CritTier {
  if (eventType === 'device_restart' || eventType === 'device.restart') return 'warning';
  switch (severity) {
    case 'error':   return 'critical';
    case 'warn':
    case 'warning': return 'warning';
    case 'debug':   return 'trace';
    case 'info':
    default:        return 'info';
  }
}

function timeAgo(dateStr: string): string {
  const now = new Date();
  const then = new Date(dateStr);
  const diffMs = now.getTime() - then.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  return `${diffDay}d ago`;
}

/**
 * Format an ISO-8601 timestamp from a device, preserving the original
 * timezone offset rather than converting to the browser's local time.
 *
 * Example: "2026-04-07T14:30:00-08:00" → "Apr 7, 2026, 2:30:00 PM (UTC-08:00)"
 *
 * We parse the string manually instead of using `new Date(...)` because Date
 * normalizes everything to UTC and loses the offset — and the whole point is
 * to show the wall-clock time as the device experienced it.
 */
function formatDeviceLocal(isoStr: string | null | undefined): string {
  if (!isoStr) return '—';
  // ISO format: YYYY-MM-DDTHH:MM:SS(.fff)?(Z|±HH:MM)
  const m = isoStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/);
  if (!m) return isoStr;
  const [, y, mo, d, h, mi, s, off] = m;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const hourNum = parseInt(h, 10);
  const ampm = hourNum >= 12 ? 'PM' : 'AM';
  const hour12 = hourNum % 12 || 12;
  const offsetLabel = !off ? '' : off === 'Z' ? ' (UTC)' : ` (UTC${off.replace(':', '')})`;
  return `${months[parseInt(mo, 10) - 1]} ${parseInt(d, 10)}, ${y}, ${hour12}:${mi}:${s} ${ampm}${offsetLabel}`;
}

// Firmware reset reason codes \u2014 see docs/SB6_MQTT_Protocol.md \u00a710.7
const RESET_REASON_BY_CODE: Record<number, string> = {
  0x00: 'Power-on',
  0x01: 'Reset pin',
  0x02: 'Watchdog',
  0x04: 'GPIO wake',
  0x08: 'CPU lockup',
  0x10: 'Software',
  0x10000: 'Debugger',
};
const RESET_REASON_BY_NAME: Record<string, string> = {
  'power-on': 'Power-on',
  'pin':      'Reset pin',
  'watchdog': 'Watchdog',
  'gpio':     'GPIO wake',
  'lockup':   'CPU lockup',
  'software': 'Software',
  'brownout': 'Brownout',
  'ctrlap':   'Debugger',
};

function formatResetReason(reason: unknown, reasonCode: unknown): string | null {
  if (typeof reason === 'string' && RESET_REASON_BY_NAME[reason]) return RESET_REASON_BY_NAME[reason];
  if (typeof reasonCode === 'number' && RESET_REASON_BY_CODE[reasonCode]) return RESET_REASON_BY_CODE[reasonCode];
  if (typeof reason === 'string' && reason) return reason;
  return null;
}

// Plain-English descriptions shown as the tooltip on the event chip. Keep
// each one to a single sentence — admins read these at-a-glance.
const EVENT_DESCRIPTIONS: Record<string, string> = {
  access_granted:      'Credential matched an active shift; the door released for the latch interval.',
  access_denied:       'Credential was rejected — unknown card, outside its shift, or blocked by lockdown.',
  wake_up:             'Device woke from sleep and reconnected to MQTT so the cloud can push any pending commands.',
  sleep_confirmed:     'Device finished its telemetry cycle and disconnected from MQTT to save power.',
  unlock_confirmed:    'Device acknowledged a momentary unlock command and triggered the door motor.',
  lock_state_changed:  'Door lock state changed — either by an active shift or an explicit command override.',
  device_reboot:       'Device rebooted on command from the cloud.',
  factory_reset:       'Device cleared all stored credentials, shifts, and holidays, then rebooted.',
  auto_reconnect:      'Device recovered an unhealthy MQTT connection by reconnecting on its own.',
  auto_recovery:       'Device performed a self-healing cold reboot after repeated communication failures.',
  command_sent:        'Cloud sent a command to the device over MQTT.',
  command_failed:      'A command sent to the device did not complete successfully.',
  provision_confirmed: 'Device accepted a provisioning payload (reader type + latch interval).',
  reader_wedged:       'The OSDP keypad is stuck and flooding the device with malformed input — only a physical power cycle of the device clears it.',
  recovery_boot:       'Device recovered from a crash (watchdog or CPU lockup) by rebooting itself — repeated occurrences point to a firmware or power problem.',
  battery_low:         'Battery crossed into the low range — plan a battery replacement soon.',
  battery_dead:        'Battery is critically depleted; the device entered safe mode and the motor can no longer move the lock. Replace the batteries now.',
  battery_recovered:   'Battery is back at a healthy level (fresh batteries or recharge) — the earlier low/dead alarm is cleared.',
  battery_swap:        'Device detected a battery replacement and reset its charge tracking.',
  device_offline:      'Device missed its expected check-in window and is considered unreachable — check power, batteries, and cellular coverage at the site.',
  device_online:       'Device checked in again after being offline — connectivity is restored.',
};

// Firmware reset-reason descriptions — used for the device_restart tooltip
// when the reason is known. Falls back to the generic device_restart blurb.
const RESET_REASON_DESCRIPTIONS: Record<string, string> = {
  'power-on': 'Cold boot from a powered-off state.',
  'pin':      'Hardware reset pin was asserted.',
  'watchdog': 'Watchdog timer expired — firmware stopped responding and the chip self-reset.',
  'gpio':     'A configured GPIO line triggered a wake / reset.',
  'lockup':   'CPU lockup detected — the chip self-reset to recover.',
  'software': 'Firmware called sys_reboot() — a planned restart (e.g. after FOTA or a cloud reboot command).',
  'brownout': 'Supply voltage dipped below the safe threshold and the device reset. Common with weak batteries or unstable power.',
  'ctrlap':   'Debugger reset (development only).',
};

const DEVICE_RESTART_DESCRIPTION =
  'Device booted and reported its reset reason. Frequent restarts can indicate power or firmware issues.';

function eventDescription(eventType: string, data: Record<string, any> | undefined): string | undefined {
  // For device_restart, prefer the reason-specific description when available.
  if (eventType === 'device_restart' || eventType === 'device.restart') {
    const reason = data?.reason;
    if (typeof reason === 'string' && RESET_REASON_DESCRIPTIONS[reason]) {
      return RESET_REASON_DESCRIPTIONS[reason];
    }
    return DEVICE_RESTART_DESCRIPTION;
  }
  return EVENT_DESCRIPTIONS[eventType];
}

function summarizeEventData(eventType: string | undefined, data: Record<string, any>): string {
  if (!data || Object.keys(data).length === 0) return '\u2014';

  // Sub-category by event type. Both dot- and underscore-forms are supported
  // because the gateway emits underscore (`lock_state_changed`) while seeds and
  // some integrations use dot-form (`lock.state_changed`).
  switch (eventType) {
    case 'lock.state_changed':
    case 'lock_state_changed':
      return doorLabel(data.lockState ?? data.state);

    case 'access.granted':
    case 'access_granted': {
      const cred = data.credential || data;
      if (cred?.pin) return 'PIN';
      const card = cred?.cardNumber ?? data.card_number;
      return card != null ? `Card ${maskCard(card)}` : 'Granted';
    }

    case 'access.denied':
    case 'access_denied': {
      const cred = data.credential || data;
      const reasonSuffix = data.reason ? ` \u00b7 ${data.reason}` : '';
      if (cred?.pin) return `PIN${reasonSuffix}`;
      const card = cred?.cardNumber ?? data.card_number;
      return card != null ? `Card ${maskCard(card)}${reasonSuffix}` : `Denied${reasonSuffix}`;
    }

    case 'device.restart':
    case 'device_restart': {
      const fwLabel = formatResetReason(data.reason, data.reasonCode);
      const baseLabel =
        fwLabel
        ?? ((data.isFactoryReset || data.code === 2) ? 'Factory reset'
        :  (data.code === 1) ? 'Reboot'
        :  'Restarted');
      const brownouts = data.brownoutCount;
      if (typeof brownouts === 'number' && brownouts > 0) {
        return `${baseLabel} · ${brownouts} brownout${brownouts === 1 ? '' : 's'}`;
      }
      return baseLabel;
    }

    case 'device.reboot':
    case 'device_reboot':
      return 'Reboot';

    case 'factory.reset':
    case 'factory_reset':
      return 'Factory reset';

    case 'auto_reconnect':
      return 'Auto reconnect';

    case 'auto_recovery':
      return 'Auto recovery';

    case 'device.online':  return 'Online';
    case 'device.offline': return 'Offline';
    case 'device.deployed': return 'Deployed';
    case 'device.data_received': return 'Telemetry received';
    case 'device.config_updated': return 'Config updated';
    case 'device.firmware_update':
      return data.version ? `Firmware ${data.version}` : 'Firmware update';

    case 'unlock.confirmed':
    case 'unlock_confirmed':
      return 'Unlock confirmed';

    case 'provision.confirmed':
    case 'provision_confirmed':
      return 'Provision confirmed';

    case 'command.queued':
    case 'command.sent':
    case 'command_sent': {
      if (data.command) {
        const via = data.source === 'api_key' ? ' via API key' : data.source === 'user' ? ' via UI' : '';
        return `${data.command}${via}`;
      }
      return eventType.split(/[._]/).pop() || 'Command';
    }

    // Failures carry the reason that matters for troubleshooting — surface it.
    case 'command.failed':
    case 'command_failed':
    case 'command_error': {
      const cmd = data.command ? String(data.command) : 'Command';
      const err = data.error || data.reason;
      return err ? `${cmd} — ${err}` : `${cmd} failed`;
    }

    case 'reader_wedged': {
      const n = data.bufferFullCount;
      return typeof n === 'number' ? `Keypad stuck · ${n} buffer-full${n === 1 ? '' : 's'}` : 'Keypad stuck';
    }

    case 'sleep_timeout':
      return 'Did not sleep when expected';

    case 'battery_swap':
      return 'Battery swapped (coulomb reset)';

    case 'recovery_boot': {
      const label = formatResetReason(data.reason, data.reasonCode);
      return label ? `Crash recovery · ${label}` : 'Crash recovery';
    }

    case 'battery_low':
      return data.batteryPct != null ? `Battery low · ${data.batteryPct}%` : 'Battery low';
    case 'battery_dead':
      return data.batteryPct != null ? `Battery dead · ${data.batteryPct}% · safe mode` : 'Battery dead · safe mode';
    case 'battery_recovered':
      return data.batteryPct != null ? `Battery recovered · ${data.batteryPct}%` : 'Battery recovered';

    case 'device_offline':
      return 'Missed check-in window';
    case 'device_online':
      return 'Back online';

    case 'cell_diagnostics': {
      const parts: string[] = [];
      if (data.signal != null) parts.push(`${data.signal} dBm`);
      if (data.carrier) parts.push(String(data.carrier));
      return parts.length ? parts.join(' · ') : 'Cell diagnostics';
    }
  }

  // Generic fallback for unknown / simple event types.
  if (data.message) return String(data.message);
  if (data.reason) return String(data.reason);
  if (data.error) return String(data.error);
  if (data.command) {
    const via = data.source === 'api_key' ? ' via API key' : data.source === 'user' ? ' via UI' : '';
    return `${data.command}${via}`;
  }
  // First informative scalar — skip booleans and fields that duplicate other
  // columns (bare timestamps/codes, or a flag whose meaning is already the type,
  // e.g. wake_up {awake:true} / device_deployed {deployed:true}). Better an
  // em-dash than a lone "true" or "120000".
  const SKIP = new Set(['timestamp', 'code', 'awake', 'deployed']);
  for (const [k, v] of Object.entries(data)) {
    if (SKIP.has(k) || typeof v === 'boolean') continue;
    const display = typeof v === 'object' ? JSON.stringify(v) : String(v);
    return display.length > 60 ? display.substring(0, 57) + '...' : display;
  }
  return '—';
}

function doorLabel(state: any): string {
  switch (String(state)) {
    case '0': case 'locked':   return 'Locked';
    case '1': case 'unlocked': return 'Unlocked';
    case '2': case 'lockdown': return 'Lockdown';
    default: return state != null ? String(state) : '—';
  }
}

// power_state from device: 0=active, 1=sleep, 2=deep_sleep
function modeVariant(powerState?: number): Variant {
  switch (powerState) {
    case 0: return 'positive';
    case 1: return 'info';
    case 2: return 'neutral';
    default: return 'neutral';
  }
}

function gatewayStatusVariant(status?: string): Variant {
  if (status === 'active' || status === 'online') return 'positive';
  if (status === 'error' || status === 'offline') return 'critical';
  if (status === 'pending' || status === 'inactive') return 'warning';
  return 'neutral';
}

// maskCard remains used by summarizeEventData (access.granted / access.denied)
function maskCard(value: unknown): string {
  if (value == null) return '—';
  const s = String(value);
  return s.length > 4 ? `····${s.slice(-4)}` : s;
}

const MODE_LABELS: Record<number, string> = {
  0: 'Active',
  1: 'Sleep',
  2: 'Deep Sleep',
};

const CARD_TYPE_LABELS: Record<number, string> = {
  0: '26-bit Wiegand',
  1: '32-bit HID',
  2: 'Mifare Classic 1k',
};

const OVERRIDE_LABELS: Record<number, string> = {
  0: 'Shifts Active',
  1: 'Shifts Overridden',
  2: 'Holiday',
};

function overrideVariant(override?: number): Variant {
  switch (override) {
    case 0: return 'positive';
    case 1: return 'warning';
    case 2: return 'info';
    default: return 'neutral';
  }
}

const OSDP_LABELS: Record<number, string> = {
  0: 'Root',
  1: 'SCS12',
  2: 'SCS14',
  3: 'Connected',
};

function osdpVariant(stage?: number): Variant {
  if (stage === 3) return 'positive';
  if (stage !== undefined && stage >= 0) return 'warning';
  return 'neutral';
}

function queueStatusVariant(status: string): Variant {
  switch (status) {
    case 'pending':    return 'warning';
    case 'processing': return 'info';
    case 'sent':       return 'positive';
    case 'failed':     return 'critical';
    default:           return 'neutral';
  }
}

function formatCommandType(type: string): string {
  // bwUnlock → Unlock, bw_cred_set → Cred Set, bwPing → Ping
  return type
    .replace(/^bw_?/, '')
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function doorVariant(state: any): Variant {
  switch (String(state)) {
    case '0': case 'locked':   return 'positive';
    case '1': case 'unlocked': return 'warning';
    case '2': case 'lockdown': return 'critical';
    default: return 'neutral';
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

function DeviceDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Existing state
  const [command, setCommand] = useState('');
  const [payload, setPayload] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: '' });
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [criticalityFilter, setCriticalityFilter] = useState<'all' | CritTier>('all');
  const [directionFilter, setDirectionFilter] = useState<'all' | 'inbound' | 'outbound' | 'internal'>('all');
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [expandedIssueId, setExpandedIssueId] = useState<string | null>(null);
  const [eventsPage, setEventsPage] = useState(0);
  const [pendingGroupOpen, setPendingGroupOpen] = useState(false);
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [sentGroupOpen, setSentGroupOpen] = useState(false);
  const [expandedCmdId, setExpandedCmdId] = useState<number | null>(null);
  const [eventsGroupOpen, setEventsGroupOpen] = useState(false);
  const [issuesGroupOpen, setIssuesGroupOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sentSearch, setSentSearch] = useState('');
  const [activeQuickAction, setActiveQuickAction] = useState<string | null>(null);
  const [quickActionFeedback, setQuickActionFeedback] = useState<{ success: boolean; message: string } | null>(null);
  const [telemetryWindow, setTelemetryWindow] = useState<TelemetryWindow>('24h');
  const [editingHardware, setEditingHardware] = useState(false);
  const [hwForm, setHwForm] = useState<HardwareForm>({
    board_id: '', hardware_version: '', num_doors: '1', power_type: 'battery', battery_chemistry: '', reader_protocol: '', reader_technology: '', company_id: '',
  });

  const isSearching = searchQuery.trim().length > 0;

  const userIsSuperAdmin = isSuperAdmin(user);
  const userIsCompanyAdmin = isCompanyAdmin(user);

  // Board picker options for the Hardware panel (any dashboard user may read
  // the catalog; only admins see the edit form).
  const { data: boards = [] } = useQuery({
    queryKey: ['device-boards'],
    queryFn: getDeviceBoards,
    enabled: userIsCompanyAdmin || userIsSuperAdmin,
  });

  // Software-provider list for the assignment control (super admin only).
  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: getCompanies,
    enabled: userIsSuperAdmin,
  });
  const assignableCompanies: Array<{ id: string; name: string }> = (companiesData as any)?.companies || [];

  // Match the server-side retention window (device_event_logs are pruned after
  // 14 days by the deviceEventRetention job / DEVICE_EVENT_RETENTION_DAYS) so we
  // don't advertise a range that has already been swept empty.
  const EVENT_WINDOW_DAYS = 14;
  const startDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - EVENT_WINDOW_DAYS);
    return d.toISOString();
  }, []);

  const oneDayAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString();
  }, []);

  // ─── Queries ───────────────────────────────────────────────────────────────

  const { data: device, isLoading } = useQuery({
    queryKey: ['device', id],
    queryFn: () => getDevice(id),
  });

  // Display name for the device's software provider. company_name comes on the
  // device row itself when the API provides it; the companies lookup is the
  // super-admin fallback. (Must sit below the device query — this component is
  // one scope, so referencing `device` earlier is a temporal-dead-zone crash.)
  const assignedCompanyName = device?.company_name
    || assignableCompanies.find((c) => c.id === device?.company_id)?.name
    || null;

  const { data: deviceState, isLoading: deviceStateLoading } = useQuery({
    queryKey: ['device-state', id],
    queryFn: () => getDeviceState(id),
    refetchInterval: 30000,
  });

  // When searching, fetch a much larger window so matches aren't trapped
  // behind pagination. Otherwise stick to the paged window.
  const { data: eventsResponse, isLoading: eventsLoading } = useQuery({
    queryKey: ['device-events', device?.device_id, isSearching ? 'search' : eventsPage, startDate],
    queryFn: () => getDeviceEvents(device!.device_id, {
      limit: isSearching ? 1000 : EVENTS_PER_PAGE,
      offset: isSearching ? 0 : eventsPage * EVENTS_PER_PAGE,
      startDate,
    }),
    enabled: !!device?.device_id,
    refetchInterval: 8000,
  });

  // Recompute the 24h window each render so the cutoff stays current. The
  // value gets stable-ified inside the query key via toISOString().
  const queueSince = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString();
  }, [id]);

  const { data: queueResponse, isLoading: queueLoading } = useQuery({
    queryKey: ['device-queue', id, queueSince],
    queryFn: () => getDeviceQueue(id as any, { since: queueSince, limit: 500 }),
    refetchInterval: 15000,
  });

  // Lightweight 24h query — used only for the Recent Issues panel
  const { data: issuesResponse } = useQuery({
    queryKey: ['device-issues', device?.device_id, oneDayAgo],
    queryFn: () => getDeviceEvents(device!.device_id, { limit: 50, startDate: oneDayAgo }),
    enabled: !!device?.device_id,
    refetchInterval: 30000,
  });

  // Gateways for the device's company — drives the Connectivity card
  const { data: gateways = [] } = useQuery({
    queryKey: ['gateways-for-company', device?.company_id],
    queryFn: () => getGatewaysByCompany(device!.company_id!),
    enabled: !!device?.company_id,
    refetchInterval: 30000,
  });

  const { data: telemetryResponse, isLoading: telemetryLoading } = useQuery({
    queryKey: ['device-telemetry', id, telemetryWindow],
    queryFn: () => getDeviceTelemetry(id, telemetryWindow),
    enabled: !!id,
    refetchInterval: 30000,
  });

  // ─── Derived data ──────────────────────────────────────────────────────────

  const events = eventsResponse?.data ?? [];
  const totalEvents = eventsResponse?.pagination?.total ?? 0;
  const totalPages = Math.ceil(totalEvents / EVENTS_PER_PAGE);

  const search = searchQuery.trim().toLowerCase();
  const filteredEvents = events.filter(e => {
    if (directionFilter !== 'all' && getDirection(e.event_type) !== directionFilter) return false;
    const tier = criticalityTier(e.event_type, e.severity);
    if (criticalityFilter === 'all') {
      if (tier === 'trace') return false; // orchestration traces hidden unless explicitly selected
    } else if (tier !== criticalityFilter) {
      return false;
    }
    if (!search) return true;
    if (e.event_type?.toLowerCase().includes(search)) return true;
    if (e.event_category?.toLowerCase().includes(search)) return true;
    if (summarizeEventData(e.event_type, e.event_data).toLowerCase().includes(search)) return true;
    if (JSON.stringify(e.event_data ?? {}).toLowerCase().includes(search)) return true;
    return false;
  });

  const recentIssues = useMemo(() =>
    (issuesResponse?.data ?? []).filter(e => e.severity === 'error' || e.severity === 'warn'),
    [issuesResponse]
  );

  const queueCommands: QueuedCommand[] = queueResponse?.commands ?? [];
  const pendingCommands = queueCommands.filter(c => c.status === 'pending' || c.status === 'processing');
  const sentCommands = queueCommands.filter(c => c.status === 'sent' || c.status === 'failed' || c.status === 'cancelled');
  const pendingCount = pendingCommands.length;

  const sentSearchLower = sentSearch.trim().toLowerCase();
  const filteredSentCommands = !sentSearchLower
    ? sentCommands
    : sentCommands.filter(cmd => {
        if (cmd.command_type?.toLowerCase().includes(sentSearchLower)) return true;
        if (formatCommandType(cmd.command_type).toLowerCase().includes(sentSearchLower)) return true;
        if (cmd.error_message?.toLowerCase().includes(sentSearchLower)) return true;
        if (cmd.status?.toLowerCase().includes(sentSearchLower)) return true;
        if (JSON.stringify(cmd.payload ?? {}).toLowerCase().includes(sentSearchLower)) return true;
        return false;
      });

  // Flatten the curated /state snapshot back into the snake_case shape the
  // Health card markup already reads. "Last wake" maps to lastSeen — for these
  // sleep-cycle devices the gateway bumps last_seen on each wake.
  const s = deviceState ? {
    firmware_version: deviceState.firmware,
    battery_pct: deviceState.batteryPct,
    power_state: deviceState.power?.current,
    door_state: deviceState.lock?.state,
    override: deviceState.lock?.override,
    card_type: deviceState.config?.cardType,
    latch_interval: deviceState.config?.latchInterval,
    last_wake_up: deviceState.lastSeen,
    credential_count: deviceState.counts?.credentials,
    shift_count: deviceState.counts?.shifts,
    door_shift_count: deviceState.counts?.doorShifts,
    holiday_count: deviceState.counts?.holidays,
    osdp_stage: deviceState.osdpStage,
    signal: deviceState.connectivity?.signal,
  } : undefined;

  // ─── Mutations ─────────────────────────────────────────────────────────────

  const commandMutation = useMutation({
    mutationFn: ({ command, payload }) => sendCommand(id, command, payload ? JSON.parse(payload) : {}),
    onSuccess: () => {
      queryClient.invalidateQueries(['device-state', id]);
      setCommand('');
      setPayload('');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { name: string }) => updateDevice(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['device', id]);
      setIsEditing(false);
      setSuccessMessage('Device updated successfully');
      setError('');
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to update device');
      setSuccessMessage('');
    },
  });

  const hardwareMutation = useMutation({
    mutationFn: (data: UpdateDeviceData) => updateDevice(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device', id] });
      setEditingHardware(false);
      setSuccessMessage('Hardware details updated');
      setError('');
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to update hardware details');
      setSuccessMessage('');
    },
  });

  const cancelCommandMutation = useMutation({
    mutationFn: (commandId: number) => cancelDeviceCommand(Number(id), commandId),
    onSuccess: () => {
      queryClient.invalidateQueries(['device-queue', id] as any);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to cancel command');
      setTimeout(() => setError(''), 4000);
    },
  });

  const quickActionMutation = useMutation({
    mutationFn: ({ command }: { action: string; command: string }) =>
      sendCommand(id as any, command, {}),
    onSuccess: (_data, { action }) => {
      setActiveQuickAction(null);
      setQuickActionFeedback({ success: true, message: `${action} queued` });
      queryClient.invalidateQueries(['device-queue', id] as any);
      queryClient.invalidateQueries(['device-state', id] as any);
      setTimeout(() => setQuickActionFeedback(null), 4000);
    },
    onError: (err: any, { action }) => {
      setActiveQuickAction(null);
      setQuickActionFeedback({
        success: false,
        message: err.response?.data?.error || err.message || `${action} failed`,
      });
    },
  });

  function handleQuickAction(action: string, cmd: string, requireConfirm = false) {
    if (requireConfirm && !window.confirm(`Send ${action} command to this device?`)) return;
    setActiveQuickAction(action);
    quickActionMutation.mutate({ action, command: cmd });
  }

  const statusVariant = (s) => {
    switch (s) {
      case 'online':  return 'green';
      case 'offline': return 'red';
      default:        return 'yellow';
    }
  };

  // ─── Loading / not found ───────────────────────────────────────────────────

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingText>Loading device...</LoadingText>
      </PageContainer>
    );
  }

  if (!device) {
    return (
      <PageContainer>
        <EmptyState>
          <p>Device not found</p>
        </EmptyState>
      </PageContainer>
    );
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <PageContainer>
      <DeviceHeader>
        <SpaceBetween>
          <div>
            <PageTitle>{device.name || 'Unnamed Device'}</PageTitle>
            <p><InlineCode>{device.device_id}</InlineCode></p>
          </div>
          <HeaderActions>
            {device.deployment_batch && (
              <StatusChip variant="purple">{device.deployment_batch}</StatusChip>
            )}
            {(userIsCompanyAdmin || userIsSuperAdmin) && !isEditing && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => { setEditForm({ name: device.name || '' }); setIsEditing(true); }}
              >
                Edit Device
              </Button>
            )}
            {(userIsCompanyAdmin || userIsSuperAdmin) && (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={activeQuickAction !== null}
                  onClick={() => handleQuickAction('Reboot', 'bwReset', true)}
                >
                  <IconRefresh size={14} stroke={2} />
                  {activeQuickAction === 'Reboot' ? 'Sending…' : 'Reboot'}
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  disabled={activeQuickAction !== null}
                  onClick={() => handleQuickAction('Factory Reset', 'bwFactory', true)}
                >
                  <IconAlertTriangle size={14} stroke={2} />
                  {activeQuickAction === 'Factory Reset' ? 'Sending…' : 'Factory Reset'}
                </Button>
              </>
            )}
          </HeaderActions>
        </SpaceBetween>
        {device.company_name && (
          <p><MutedText>{device.company_name}</MutedText></p>
        )}

        {/* Reboot / Factory Reset feedback */}
        {quickActionFeedback && (
          quickActionFeedback.success
            ? <SuccessText>{quickActionFeedback.message}</SuccessText>
            : <ErrorText>{quickActionFeedback.message}</ErrorText>
        )}

        {/* Edit save feedback (kept visible briefly even after the form closes) */}
        {successMessage && <AlertBanner variant="success">{successMessage}</AlertBanner>}
        {error && <AlertBanner variant="error">{error}</AlertBanner>}

        {/* Inline rename — company admin and up, opened by the Edit button
            above (the PATCH endpoint scopes company admins to their own
            company). Enter saves, Escape cancels; icon buttons mirror both. */}
        {isEditing && (userIsCompanyAdmin || userIsSuperAdmin) && (
          <EditNameRow>
            <EditNameInput
              autoFocus
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              placeholder={device.name || 'Device name'}
              disabled={updateMutation.isPending}
              onKeyDown={(e) => {
                if (e.key === 'Enter') updateMutation.mutate(editForm);
                if (e.key === 'Escape') setIsEditing(false);
              }}
            />
            <IconButton
              type="button"
              title="Save"
              aria-label="Save name"
              onClick={() => updateMutation.mutate(editForm)}
              disabled={updateMutation.isPending}
            >
              <IconCheck size={16} stroke={2} />
            </IconButton>
            <IconButton
              type="button"
              title="Cancel"
              aria-label="Cancel edit"
              onClick={() => setIsEditing(false)}
              disabled={updateMutation.isPending}
            >
              <IconX size={16} stroke={2} />
            </IconButton>
          </EditNameRow>
        )}
      </DeviceHeader>

      {/* ── Recent Issues ─────────────────────────────────────────────────── */}
      {/* Only shown when there are errors or warnings in the last 24 hours. */}
      {recentIssues.length > 0 && (
        <Section>
          <DetailCard>
            <CollapsibleHeader onClick={() => setIssuesGroupOpen(o => !o)}>
              <h2>Issues in the last 24 hours</h2>
              <Row gap={2} align="center">
                <StatusChip variant="critical">
                  {recentIssues.length} issue{recentIssues.length !== 1 ? 's' : ''}
                </StatusChip>
                <Chevron open={issuesGroupOpen} />
              </Row>
            </CollapsibleHeader>
            {issuesGroupOpen && (
              <CardBody>
                <TableContainer>
                  <StyledTable>
                    <thead>
                      <tr>
                        <Th>Time</Th>
                        <Th>Severity</Th>
                        <Th>Type</Th>
                        <Th>Summary</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentIssues.slice(0, 5).map(issue => {
                        const isExpanded = expandedIssueId === issue.id;
                        return (
                          <React.Fragment key={issue.id}>
                            <TableRow
                              onClick={() => setExpandedIssueId(isExpanded ? null : issue.id)}
                              title="Click to expand"
                            >
                              <Td title={new Date(issue.created_at).toLocaleString()}>
                                {timeAgo(issue.created_at)}
                              </Td>
                              <Td>
                                <StatusChip variant={severityVariant(issue.severity)}>
                                  {issue.severity}
                                </StatusChip>
                              </Td>
                              <Td>{formatEventType(issue.event_type)}</Td>
                              <TruncatedTd>{summarizeEventData(issue.event_type, issue.event_data)}</TruncatedTd>
                            </TableRow>
                            {isExpanded && (
                              <EventDataRow>
                                <td colSpan={4}>
                                  <ExpandedCodeBlock>
                                    {JSON.stringify(issue.event_data, null, 2)}
                                  </ExpandedCodeBlock>
                                </td>
                              </EventDataRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </StyledTable>
                </TableContainer>
                {recentIssues.length > 5 && (
                  <FootnoteText>
                    and {recentIssues.length - 5} more — see Live Events below
                  </FootnoteText>
                )}
              </CardBody>
            )}
          </DetailCard>
        </Section>
      )}

      {/* ── Unified Device card ───────────────────────────────────────────── */}
      <Section>
        <DetailCard>
          <h2>Device</h2>

          {/* Details */}
          <CollapsibleSubsection title="Details">
            <HealthGrid>
              <HealthMetric>
                <HealthLabel>Status</HealthLabel>
                <StatusChip variant={statusVariant(device.status)}>
                  {device.status}
                </StatusChip>
              </HealthMetric>

              <HealthMetric>
                <HealthLabel>Last Seen</HealthLabel>
                <SmallHealthValue title={device.last_seen ? new Date(device.last_seen).toLocaleString() : undefined}>
                  {device.last_seen_local
                    ? formatDeviceLocal(device.last_seen_local)
                    : device.last_seen
                      ? new Date(device.last_seen).toLocaleString()
                      : 'Never'}
                </SmallHealthValue>
              </HealthMetric>

              <HealthMetric>
                <HealthLabel>Created</HealthLabel>
                <SmallHealthValue>{new Date(device.created_at).toLocaleString()}</SmallHealthValue>
              </HealthMetric>

              <HealthMetric>
                <HealthLabel>Deployed</HealthLabel>
                <Row gap={2} align="center">
                  <DeployedBadge deployed={device.deployed} />
                  {device.deployed_at && (
                    <Caption>{new Date(device.deployed_at).toLocaleDateString()}</Caption>
                  )}
                </Row>
              </HealthMetric>

              <HealthMetric>
                <HealthLabel>Firmware</HealthLabel>
                <SmallHealthValue>{s?.firmware_version || device.metadata?.firmware || '—'}</SmallHealthValue>
              </HealthMetric>
            </HealthGrid>
          </CollapsibleSubsection>

          {/* Hardware — board catalog identity plus per-unit provisioning facts.
              The board decides the capabilities[] / features{} the customer
              API publishes for this device; the rest are set at install time. */}
          <CollapsibleSubsection
            title="Hardware"
            accessory={(userIsCompanyAdmin || userIsSuperAdmin) && !editingHardware ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setHwForm({
                    board_id: device.board_id != null ? String(device.board_id) : '',
                    hardware_version: device.hardware_version || '',
                    num_doors: String(device.num_doors ?? 1),
                    power_type: device.power_type || 'battery',
                    battery_chemistry: device.battery_chemistry || '',
                    reader_protocol: device.reader_protocol || '',
                    reader_technology: device.reader_technology || '',
                    company_id: device.company_id != null ? String(device.company_id) : '',
                  });
                  setEditingHardware(true);
                }}
              >
                Edit
              </Button>
            ) : undefined}
          >
            {editingHardware ? (
              <form onSubmit={(e) => { e.preventDefault(); hardwareMutation.mutate(toHardwareUpdate(hwForm, { includeAssignment: userIsSuperAdmin })); }}>
                <HardwareFormGrid>
                  {userIsSuperAdmin && (
                    <FormGroup>
                      <Label>Software provider</Label>
                      <Select
                        value={hwForm.company_id}
                        onChange={(e) => setHwForm({ ...hwForm, company_id: e.target.value })}
                      >
                        <option value="">Unassigned pool</option>
                        {assignableCompanies.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </Select>
                    </FormGroup>
                  )}
                  <FormGroup>
                    <Label>Board</Label>
                    <Select
                      value={hwForm.board_id}
                      onChange={(e) => setHwForm({ ...hwForm, board_id: e.target.value })}
                      required
                    >
                      <option value="" disabled>Select a board…</option>
                      {boards.map((b) => (
                        <option key={b.id} value={b.id}>{boardLabel(b)}</option>
                      ))}
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Hardware revision</Label>
                    <Input
                      value={hwForm.hardware_version}
                      onChange={(e) => setHwForm({ ...hwForm, hardware_version: e.target.value })}
                      placeholder="rev-b"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Doors</Label>
                    <Input
                      type="number"
                      min={1}
                      max={32}
                      value={hwForm.num_doors}
                      onChange={(e) => setHwForm({ ...hwForm, num_doors: e.target.value })}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Power</Label>
                    <Select
                      value={hwForm.power_type}
                      onChange={(e) => setHwForm({ ...hwForm, power_type: e.target.value as HardwareForm['power_type'] })}
                    >
                      <option value="battery">battery</option>
                      <option value="plugin">plugin</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Battery chemistry</Label>
                    <Select
                      value={hwForm.battery_chemistry}
                      onChange={(e) => setHwForm({ ...hwForm, battery_chemistry: e.target.value })}
                    >
                      <option value="">Unknown</option>
                      <option value="alkaline">alkaline</option>
                      <option value="lithium">lithium</option>
                      <option value="li-ion">li-ion</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Reader protocol</Label>
                    <Select
                      value={hwForm.reader_protocol}
                      onChange={(e) => setHwForm({ ...hwForm, reader_protocol: e.target.value })}
                    >
                      <option value="">Infer from device</option>
                      <option value="osdp">osdp</option>
                      <option value="wiegand">wiegand</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Reader technology</Label>
                    <Select
                      value={hwForm.reader_technology}
                      onChange={(e) => setHwForm({ ...hwForm, reader_technology: e.target.value })}
                    >
                      <option value="">Unknown</option>
                      <option value="prox">prox</option>
                      <option value="smartcard">smartcard</option>
                      <option value="nfc">nfc</option>
                      <option value="ble">ble</option>
                      <option value="multi">multi</option>
                    </Select>
                  </FormGroup>
                </HardwareFormGrid>
                <Row gap={2}>
                  <Button type="submit" size="sm" variant="primary" disabled={hardwareMutation.isPending || !hwForm.board_id}>
                    {hardwareMutation.isPending ? 'Saving…' : 'Save'}
                  </Button>
                  <Button type="button" size="sm" variant="secondary" onClick={() => setEditingHardware(false)} disabled={hardwareMutation.isPending}>
                    Cancel
                  </Button>
                </Row>
              </form>
            ) : (
              <>
                <HealthGrid>
                  <HealthMetric>
                    <HealthLabel>Software provider</HealthLabel>
                    <SmallHealthValue>
                      {device.company_id ? (assignedCompanyName || device.company_id) : 'Unassigned'}
                    </SmallHealthValue>
                  </HealthMetric>
                  <HealthMetric>
                    <HealthLabel>Board</HealthLabel>
                    <SmallHealthValue>
                      {device.board_display_name
                        || [device.board_manufacturer ?? device.manufacturer, device.board_name ?? device.board].filter(Boolean).join(' ')
                        || '—'}
                    </SmallHealthValue>
                  </HealthMetric>
                  <HealthMetric>
                    <HealthLabel>Revision</HealthLabel>
                    <SmallHealthValue>{device.hardware_version || '—'}</SmallHealthValue>
                  </HealthMetric>
                  <HealthMetric>
                    <HealthLabel>Doors</HealthLabel>
                    <SmallHealthValue>{device.num_doors ?? 1}</SmallHealthValue>
                  </HealthMetric>
                  <HealthMetric>
                    <HealthLabel>Power</HealthLabel>
                    <SmallHealthValue>
                      {device.power_type || 'battery'}{device.battery_chemistry ? ` · ${device.battery_chemistry}` : ''}
                    </SmallHealthValue>
                  </HealthMetric>
                  <HealthMetric>
                    <HealthLabel>Reader protocol</HealthLabel>
                    <SmallHealthValue>{device.reader_protocol || 'inferred'}</SmallHealthValue>
                  </HealthMetric>
                  <HealthMetric>
                    <HealthLabel>Reader technology</HealthLabel>
                    <SmallHealthValue>{device.reader_technology || '—'}</SmallHealthValue>
                  </HealthMetric>
                </HealthGrid>
                {(device.board_capabilities?.length || device.board_features) && (
                  <ChipRow>
                    {(device.board_capabilities ?? []).map((c) => (
                      <StatusChip key={c} variant="neutral">{c}</StatusChip>
                    ))}
                    {Object.entries(device.board_features ?? {}).map(([slug, on]) => (
                      <StatusChip key={slug} variant={on ? 'positive' : 'neutral'}>{slug}: {on ? 'on' : 'off'}</StatusChip>
                    ))}
                  </ChipRow>
                )}
                {!device.board_id && (userIsCompanyAdmin || userIsSuperAdmin) && (
                  <MutedText>No board assigned — the customer API treats this device as an SB6 until one is set.</MutedText>
                )}
              </>
            )}
          </CollapsibleSubsection>

          {/* Connectivity — gateway(s) routing events for this company */}
          <CollapsibleSubsection title="Connectivity">
            {gateways.length === 0 ? (
              <MutedText>No gateway configured for this company.</MutedText>
            ) : (
              <Stack gap={3}>
                {gateways.map(g => (
                  <div key={g.id}>
                    <SpaceBetween>
                      <strong>{g.name}</strong>
                      <StatusChip variant={gatewayStatusVariant(g.status)}>
                        {g.status}
                      </StatusChip>
                    </SpaceBetween>
                    {g.last_health_check && (
                      <Caption>Last check {timeAgo(g.last_health_check)}</Caption>
                    )}
                    {g.error_message && (
                      <ErrorText>{g.error_message}</ErrorText>
                    )}
                  </div>
                ))}
              </Stack>
            )}
          </CollapsibleSubsection>

          {/* Health — runtime metrics. Battery and cell signal show the current
              snapshot here, qualified for the operator; their trends live in the
              Telemetry charts below. */}
          <CollapsibleSubsection
            title="Health"
            defaultOpen
            accessory={deviceStateLoading ? <Caption>Refreshing…</Caption> : undefined}
          >
            {!deviceState && !deviceStateLoading ? (
              <EmptyState>
                <p>No status data available</p>
              </EmptyState>
            ) : (
              <HealthGrid>
                <HealthMetric>
                  <HealthLabel>Battery</HealthLabel>
                  <HealthValue className="tabular">
                    {s?.battery_pct != null ? `${Math.round(s.battery_pct)}%` : '—'}
                  </HealthValue>
                </HealthMetric>

                <HealthMetric>
                  <HealthLabel>Cell Signal</HealthLabel>
                  {s?.signal != null ? (
                    <>
                      <HealthValue className="tabular">{s.signal} dBm</HealthValue>
                      <StatusChip variant={signalQuality(s.signal).variant}>
                        {signalQuality(s.signal).label}
                      </StatusChip>
                    </>
                  ) : (
                    <HealthValue>—</HealthValue>
                  )}
                </HealthMetric>

                <HealthMetric>
                  <HealthLabel>Power State</HealthLabel>
                  <StatusChip variant={modeVariant(s?.power_state)}>
                    {MODE_LABELS[s?.power_state ?? 1] ?? 'Sleep'}
                  </StatusChip>
                </HealthMetric>

                {/* Approx. location resolved from the device's cell-tower
                    fingerprint (internal-only; from metadata.state.geo, not the
                    customer /state snapshot). Coarse — city/state, not a fix. */}
                <HealthMetric>
                  <HealthLabel>Approx. Location</HealthLabel>
                  {(() => {
                    const geo = device?.metadata?.state?.geo;
                    if (!geo?.city) return <HealthValue>—</HealthValue>;
                    return (
                      <SmallHealthValue
                        title={geo.accuracyM != null ? `±${geo.accuracyM} m (cell tower)` : 'Approx. from cell tower'}
                      >
                        {geo.city}{geo.state ? `, ${geo.state}` : ''}
                      </SmallHealthValue>
                    );
                  })()}
                </HealthMetric>

                <HealthMetric>
                  <HealthLabel>Door State</HealthLabel>
                  {s?.door_state != null ? (
                    <StatusChip variant={doorVariant(s.door_state)}>
                      {doorLabel(s.door_state)}
                    </StatusChip>
                  ) : (
                    <HealthValue>—</HealthValue>
                  )}
                </HealthMetric>

                <HealthMetric>
                  <HealthLabel>Override</HealthLabel>
                  <StatusChip variant={overrideVariant(s?.override)}>
                    {OVERRIDE_LABELS[s?.override ?? 0] ?? '—'}
                  </StatusChip>
                </HealthMetric>

                <HealthMetric>
                  <HealthLabel>Card Type</HealthLabel>
                  <SmallHealthValue>{CARD_TYPE_LABELS[s?.card_type] ?? '—'}</SmallHealthValue>
                </HealthMetric>

                <HealthMetric>
                  <HealthLabel>Latch Interval</HealthLabel>
                  <HealthValue>{s?.latch_interval != null ? `${s.latch_interval}s` : '—'}</HealthValue>
                </HealthMetric>

                <HealthMetric>
                  <HealthLabel>Last Wake</HealthLabel>
                  <SmallHealthValue>
                    {s?.last_wake_up ? timeAgo(s.last_wake_up) : '—'}
                  </SmallHealthValue>
                </HealthMetric>

                <HealthMetric>
                  <HealthLabel>Credentials</HealthLabel>
                  <HealthValue>{s?.credential_count ?? '—'}</HealthValue>
                </HealthMetric>

                <HealthMetric>
                  <HealthLabel>Shifts</HealthLabel>
                  <HealthValue>{s?.shift_count ?? '—'}</HealthValue>
                </HealthMetric>

                <HealthMetric>
                  <HealthLabel>Door Shifts</HealthLabel>
                  <HealthValue>{s?.door_shift_count ?? '—'}</HealthValue>
                </HealthMetric>

                <HealthMetric>
                  <HealthLabel>Holidays</HealthLabel>
                  <HealthValue>{s?.holiday_count ?? '—'}</HealthValue>
                </HealthMetric>

                <HealthMetric>
                  <HealthLabel>OSDP</HealthLabel>
                  <StatusChip variant={osdpVariant(s?.osdp_stage)}>
                    {OSDP_LABELS[s?.osdp_stage] ?? '—'}
                  </StatusChip>
                </HealthMetric>
              </HealthGrid>
            )}
          </CollapsibleSubsection>

          {/* Telemetry — battery + signal as time-series, with selectable window */}
          <CollapsibleSubsection
            title="Telemetry"
            accessory={
              <SegmentedTabs
                size="sm"
                tabs={[
                  { id: '24h', label: '24h' },
                  { id: '7d', label: '7d' },
                  { id: '30d', label: '30d' },
                ]}
                activeId={telemetryWindow}
                onChange={(id) => setTelemetryWindow(id as TelemetryWindow)}
              />
            }
          >
            {(() => {
              const samples = telemetryResponse?.samples ?? [];
              if (telemetryLoading && samples.length === 0) {
                return <LoadingText>Loading telemetry…</LoadingText>;
              }
              return <TelemetryChart samples={samples} />;
            })()}
          </CollapsibleSubsection>
        </DetailCard>
      </Section>

      {/* Raw metadata — advanced / raw dump, collapsed by default */}
      <Section>
        <DetailCard>
          <CollapsibleHeader onClick={() => setMetadataOpen(o => !o)}>
            <h2>Raw metadata</h2>
            <Chevron open={metadataOpen} />
          </CollapsibleHeader>
          {metadataOpen && (
            <CardBody>
              <CodeBlock>
                {JSON.stringify(device.metadata, null, 2)}
              </CodeBlock>
            </CardBody>
          )}
        </DetailCard>
      </Section>

      {/* ── Command Queue ─────────────────────────────────────────────────── */}
      <Section>
        <SectionHeader>
          <Row gap={2} align="center">
            <h2>Command Queue</h2>
            <PendingBadge count={pendingCount}>{pendingCount} pending</PendingBadge>
          </Row>
          {queueLoading && <Caption>Loading…</Caption>}
        </SectionHeader>

        {queueLoading ? null : (
          <>
            <QueueGroup>
              <QueueGroupHeader onClick={() => setPendingGroupOpen(o => !o)}>
                Pending
                <PendingBadge count={pendingCommands.length}>{pendingCommands.length}</PendingBadge>
                <Chevron open={pendingGroupOpen} />
              </QueueGroupHeader>
              {pendingGroupOpen && (
                pendingCommands.length === 0 ? (
                  <EmptyState><p>No pending commands</p></EmptyState>
                ) : (
                  <TableContainer>
                    <StyledTable>
                      <thead>
                        <tr>
                          <Th>Status</Th>
                          <Th>Command</Th>
                          <Th>Queued</Th>
                          {(userIsCompanyAdmin || userIsSuperAdmin) && <ActionTh>Actions</ActionTh>}
                        </tr>
                      </thead>
                      <tbody>
                        {pendingCommands.map(cmd => {
                          const isExpanded = expandedCmdId === cmd.id;
                          // Only true 'pending' rows are cancellable; 'processing'
                          // is mid-flight and racing the publisher would surprise.
                          const canCancel = cmd.status === 'pending'
                            && (userIsCompanyAdmin || userIsSuperAdmin);
                          return (
                            <React.Fragment key={cmd.id}>
                              <TableRow
                                onClick={() => setExpandedCmdId(isExpanded ? null : cmd.id)}
                                title="Click to expand payload"
                              >
                                <Td>
                                  <StatusChip variant={queueStatusVariant(cmd.status)}>
                                    {cmd.status}
                                  </StatusChip>
                                </Td>
                                <Td>{formatCommandType(cmd.command_type)}</Td>
                                <Td title={new Date(cmd.created_at).toLocaleString()}>
                                  {timeAgo(cmd.created_at)}
                                </Td>
                                {(userIsCompanyAdmin || userIsSuperAdmin) && (
                                  <Td onClick={(e) => e.stopPropagation()}>
                                    {canCancel ? (
                                      <Button
                                        size="sm"
                                        variant="danger"
                                        onClick={() => {
                                          if (window.confirm(`Cancel queued ${formatCommandType(cmd.command_type)}?`)) {
                                            cancelCommandMutation.mutate(cmd.id);
                                          }
                                        }}
                                        disabled={cancelCommandMutation.isPending}
                                      >
                                        <IconX size={12} stroke={2} />
                                        Cancel
                                      </Button>
                                    ) : (
                                      <TinyMutedText>—</TinyMutedText>
                                    )}
                                  </Td>
                                )}
                              </TableRow>
                              {isExpanded && (
                                <EventDataRow>
                                  <td colSpan={(userIsCompanyAdmin || userIsSuperAdmin) ? 4 : 3}>
                                    <ExpandedCodeBlock>
                                      {JSON.stringify(cmd.payload, null, 2)}
                                    </ExpandedCodeBlock>
                                  </td>
                                </EventDataRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </StyledTable>
                  </TableContainer>
                )
              )}
            </QueueGroup>

            <QueueGroup>
              <QueueGroupHeader onClick={() => setSentGroupOpen(o => !o)}>
                Sent
                <PendingBadge count={0}>{sentCommands.length}</PendingBadge>
                <Caption>last 24h</Caption>
                <Chevron open={sentGroupOpen} />
              </QueueGroupHeader>
              {sentGroupOpen && (
                <>
                  {sentCommands.length > 0 && (
                    <InCardSearchWrapper>
                      <InCardSearch
                        type="search"
                        placeholder="Search sent commands (type, error, payload)…"
                        value={sentSearch}
                        onChange={(e) => { setSentSearch(e.target.value); setExpandedCmdId(null); }}
                      />
                    </InCardSearchWrapper>
                  )}
                  {sentCommands.length === 0 ? (
                    <EmptyState><p>No sent commands in the last 24 hours</p></EmptyState>
                  ) : filteredSentCommands.length === 0 ? (
                    <EmptyState><p>No sent commands match "{sentSearch}"</p></EmptyState>
                  ) : (
                    <TableContainer>
                      <StyledTable>
                        <thead>
                          <tr>
                            <Th>Status</Th>
                            <Th>Command</Th>
                            <Th>Priority</Th>
                            <Th>Attempts</Th>
                            <Th>Queued</Th>
                            <Th>Delivered</Th>
                            <Th>Error</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredSentCommands.map(cmd => {
                            const isExpanded = expandedCmdId === cmd.id;
                            return (
                              <React.Fragment key={cmd.id}>
                                <TableRow
                                  onClick={() => setExpandedCmdId(isExpanded ? null : cmd.id)}
                                  title="Click to expand payload"
                                >
                                  <Td>
                                    <StatusChip variant={queueStatusVariant(cmd.status)}>
                                      {cmd.status}
                                    </StatusChip>
                                  </Td>
                                  <Td>{formatCommandType(cmd.command_type)}</Td>
                                  <Td>{cmd.priority ?? 0}</Td>
                                  <Td>{cmd.attempts}/{cmd.max_attempts}</Td>
                                  <Td title={new Date(cmd.created_at).toLocaleString()}>
                                    {timeAgo(cmd.created_at)}
                                  </Td>
                                  <Td title={cmd.sent_at ? new Date(cmd.sent_at).toLocaleString() : undefined}>
                                    {cmd.sent_at ? timeAgo(cmd.sent_at) : '—'}
                                  </Td>
                                  <ErrorTd
                                    title={cmd.error_message || undefined}
                                    $hasError={!!cmd.error_message}
                                  >
                                    {cmd.error_message || '—'}
                                  </ErrorTd>
                                </TableRow>
                                {isExpanded && (
                                  <EventDataRow>
                                    <td colSpan={7}>
                                      {cmd.error_message && (
                                        <ExpandedErrorText>
                                          <strong>Error:</strong> {cmd.error_message}
                                        </ExpandedErrorText>
                                      )}
                                      <ExpandedCodeBlock>
                                        {JSON.stringify(cmd.payload, null, 2)}
                                      </ExpandedCodeBlock>
                                    </td>
                                  </EventDataRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </StyledTable>
                    </TableContainer>
                  )}
                </>
              )}
            </QueueGroup>
          </>
        )}
      </Section>

      {/* ── Live Events ───────────────────────────────────────────────────── */}
      <Section>
        <QueueGroup>
          <QueueGroupHeader onClick={() => setEventsGroupOpen(o => !o)}>
            <LiveDot><IconCircleFilled size={10} /></LiveDot>Live Events
            <PendingBadge count={0}>{totalEvents}</PendingBadge>
            <Chevron open={eventsGroupOpen} />
          </QueueGroupHeader>

          {eventsGroupOpen && (
            <>
              <InCardToolbar>
                <ToolbarSearch
                  type="search"
                  placeholder="Search events…"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setExpandedEventId(null); }}
                />
                <SegmentedTabs
                  size="sm"
                  tabs={[
                    { id: 'all', label: 'All' },
                    { id: 'inbound', label: 'From Device' },
                    { id: 'outbound', label: 'To Device' },
                    { id: 'internal', label: 'System' },
                  ]}
                  activeId={directionFilter}
                  onChange={(id) => {
                    setDirectionFilter(id as 'all' | 'inbound' | 'outbound' | 'internal');
                    setEventsPage(0);
                    setExpandedEventId(null);
                  }}
                />
                <ToolbarSelect
                  value={criticalityFilter}
                  onChange={(e) => {
                    setCriticalityFilter(e.target.value as 'all' | CritTier);
                    setEventsPage(0);
                    setExpandedEventId(null);
                  }}
                  title="Filter by criticality"
                >
                  <option value="all">All criticalities</option>
                  <option value="critical">Critical</option>
                  <option value="warning">Warning</option>
                  <option value="info">Info</option>
                  <option value="trace">Trace (orchestration)</option>
                </ToolbarSelect>
              </InCardToolbar>

              <ToolbarNote>
                “Time” is the device's local clock (offset preserved as reported by firmware); “Since” counts from now.
                System events without a device timestamp fall back to server-received time. Orchestration “Trace” events are
                hidden unless selected above.
              </ToolbarNote>

              {eventsLoading ? (
                <LoadingText>Loading events...</LoadingText>
              ) : filteredEvents.length === 0 ? (
                <EmptyState>
                  <p>
                    {isSearching
                      ? `No events match "${searchQuery}"`
                      : `No events in the last ${EVENT_WINDOW_DAYS} days${directionFilter !== 'all' ? ' for this direction' : ''}`}
                  </p>
                </EmptyState>
              ) : (
                <>
                  <TableContainer>
                    <StyledTable>
                      <thead>
                        <tr>
                          <Th>Event Type</Th>
                          <Th>Time</Th>
                          <Th>Since</Th>
                          <Th>Detail</Th>
                          <Th>Direction</Th>
                          <Th>Criticality</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEvents.map(event => {
                          const dir = getDirection(event.event_type);
                          const isExpanded = expandedEventId === event.id;
                          const crit = CRIT_META[criticalityTier(event.event_type, event.severity)];
                          // Prefer the device's own timestamp; fall back to server receipt for
                          // System events the device never reported.
                          const happenedIso = event.device_ts_local ?? event.created_at;
                          const isDeviceTime = !!event.device_ts_local;
                          return (
                            <React.Fragment key={event.id}>
                              <TableRow
                                onClick={() => setExpandedEventId(isExpanded ? null : event.id)}
                                title="Click to expand event data"
                              >
                                <Td>
                                  <StatusChip
                                    variant="neutral"
                                    title={eventDescription(event.event_type, event.event_data)}
                                  >
                                    {formatEventType(event.event_type)}
                                  </StatusChip>
                                </Td>
                                <Td title={isDeviceTime ? 'Device local time' : 'Server-received time (device did not report one)'}>
                                  {formatDeviceLocal(happenedIso)}{isDeviceTime ? '' : ' (server)'}
                                </Td>
                                <Td title={formatDeviceLocal(happenedIso)}>
                                  {timeAgo(happenedIso)}
                                </Td>
                                <TruncatedTd title={JSON.stringify(event.event_data)}>
                                  {summarizeEventData(event.event_type, event.event_data)}
                                </TruncatedTd>
                                <Td>
                                  <StatusChip variant={DIR_VARIANTS[dir]}>
                                    {DIR_LABELS[dir]}
                                  </StatusChip>
                                </Td>
                                <Td>
                                  <StatusChip variant={crit.variant}>
                                    {crit.label}
                                  </StatusChip>
                                </Td>
                              </TableRow>
                              {isExpanded && (
                                <EventDataRow>
                                  <td colSpan={6}>
                                    <ExpandedCodeBlock>
                                      {JSON.stringify(event.event_data, null, 2)}
                                    </ExpandedCodeBlock>
                                  </td>
                                </EventDataRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </StyledTable>
                  </TableContainer>

                  {!isSearching && totalPages > 1 && (
                    <Pagination>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={eventsPage === 0}
                        onClick={() => setEventsPage(p => p - 1)}
                      >
                        Previous
                      </Button>
                      <span>Page {eventsPage + 1} of {totalPages}</span>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={eventsPage >= totalPages - 1}
                        onClick={() => setEventsPage(p => p + 1)}
                      >
                        Next
                      </Button>
                    </Pagination>
                  )}
                </>
              )}
            </>
          )}
        </QueueGroup>
      </Section>

    </PageContainer>
  );
}

export default DeviceDetail;
